import toast from "react-hot-toast";
import { AppDispatch, AppState } from "@/store/configureStore";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { CREATE_GROUP_CART_API } from "@/constants";
import {
	incrementProductQuantity,
	decrementProductQuantity,
	createGroupCart,
	addToGroupCart,
	deleteGroupCartProduct,
	setGroupCartLoading,
} from "@/store/slices/groupCartSlice";
import { groupCartProps, inviteeProps } from "@/interface/cart";
import { useRouter } from "next/navigation";
import FetchAllGroupCarts from "./fetchAllGroupCarts";
import useRemoveDiscountIfNeeded from "../cart/useRemoveDiscountIfNeeded";

const useGroupCartProduct = () => {
	const router = useRouter();
	const user = useSelector((state: AppState) => state.user);
	const groupCart = useSelector((state: AppState) => state.group_cart);
	const invitee = useSelector((state: AppState) => state.invitee);
	const activeCart =
		user?.groupCarts?.length &&
		user?.groupCarts?.find(
			(groupcart: groupCartProps) => groupcart?.allowShopping === true
		);
	const cartId = groupCart?.id || "";
	const inviteeToken = user?.groupCarts?.find((groupCart: groupCartProps) =>
		groupCart?.invitees?.find(
			(invitees: inviteeProps) => invitees?.token === invitee?.token
		)
	);
	const dispatch = useDispatch<AppDispatch>();
	const removeDiscountIfNeeded = useRemoveDiscountIfNeeded();

	const addItemToGroupCart = async (productId: string | undefined) => {
		// const inviteeToken = user?.groupCarts?.find((groupCart: groupCartProps) =>
		//     groupCart?.invitees?.find((invitee: inviteeProps) =>
		//         invitee?.token === data?.inviteeToken))
		const endpoint = `${CREATE_GROUP_CART_API}/${cartId}/add-product/${productId}`;
		try {
			dispatch(setGroupCartLoading(true));
			await removeDiscountIfNeeded();
			const response = await axios.post(
				endpoint,
				{},
				{
					headers: {
						Authorization: `Bearer ${user?.token}`,
					},
					params: {
						inviteeToken: inviteeToken ? inviteeToken : "",
					},
				}
			);
			// console.log(response?.data);
			dispatch(
				addToGroupCart({
					...groupCart,
					id: response?.data?.result?.id,
					allowShopping: response?.data?.result?.allowShopping,
					totalPrice: response?.data?.result?.totalPrice,
					items: response?.data?.result?.items,
					invitees: response?.data?.result?.invitees,
				})
			);
		} catch (error: any) {
			// console.log(error);
			toast.remove();
			toast.error(error.response?.data?.error);
		} finally {
			dispatch(setGroupCartLoading(false));
		}
	};

	const deleteGroupCartItem = async (productId: string | undefined) => {
		const endpoint = `${CREATE_GROUP_CART_API}/${cartId}/remove-product/${productId}`;
		// console.log(endpoint);
		// console.log(productId);
		try {
			dispatch(setGroupCartLoading(true));
			await removeDiscountIfNeeded();
			const response = await axios.delete(endpoint, {
				headers: {
					Authorization: `Bearer ${user?.token}`,
				},
				params: {
					inviteeToken: inviteeToken ? inviteeToken : "",
				},
			});
			// console.log(response?.data);
			dispatch(
				deleteGroupCartProduct({
					...groupCart,
					id: response?.data?.result?.id,
					totalPrice: response?.data?.result?.totalPrice,
					items: response?.data?.result?.items,
				})
			);
			toast.remove();
			toast.success(response?.data?.msg);
		} catch (error: any) {
			// console.log(error);
			toast.remove();
			toast.error(error?.response?.data?.error);
		} finally {
			dispatch(setGroupCartLoading(false));
		}
	};

	const increaseItemQuantity = async (productId: string | undefined) => {
		const endpoint = `${CREATE_GROUP_CART_API}/${cartId}/increase/${productId}`;
		try {
			dispatch(setGroupCartLoading(true));
			await removeDiscountIfNeeded();
			const response = await axios.put(
				endpoint,
				{},
				{
					headers: {
						Authorization: `Bearer ${user?.token}`,
					},
					params: {
						inviteeToken: inviteeToken ? inviteeToken : "",
					},
				}
			);
			dispatch(
				incrementProductQuantity({
					id: response?.data?.result?.id,
					totalPrice: response?.data?.result?.totalPrice,
					items: response?.data?.result?.items,
				})
			);
			toast.remove();
			toast.success("Cart item updated");
		} catch (error: string | any) {
			// console.log(error);
			toast.remove();
			toast.error(error.response?.data?.error);
		} finally {
			dispatch(setGroupCartLoading(false));
		}
	};

	const decreaseItemQuantity = async (productId: string | undefined) => {
		const endpoint = `${CREATE_GROUP_CART_API}/${cartId}/decrease/${productId}`;
		try {
			dispatch(setGroupCartLoading(true));
			await removeDiscountIfNeeded();
			const response = await axios.put(
				endpoint,
				{},
				{
					headers: {
						Authorization: `Bearer ${user?.token}`,
					},
					params: {
						inviteeToken: inviteeToken ? inviteeToken : "",
					},
				}
			);
			dispatch(
				decrementProductQuantity({
					id: response?.data?.result?.id,
					totalPrice: response?.data?.result?.totalPrice,
					items: response?.data?.result?.items,
				})
			);
			toast.remove();
			toast.success("Cart item updated");
		} catch (error: string | any) {
			// console.log(error);
			toast.remove();
			toast.error(error.response?.data?.error);
		} finally {
			dispatch(setGroupCartLoading(false));
		}
	};

	const endShopping = async () => {
		const endpoint = `${CREATE_GROUP_CART_API}/${cartId}/end-shopping`;
		try {
			// console.log("trying");

			// const response = await axios.put(endpoint, {}, {
			//     headers : {
			//         Authorization: `Bearer ${user?.token}`
			//     }
			// })
			// toast.success(response?.data?.msg);
			dispatch(
				createGroupCart({
					...groupCart,
					items: [],
					id: "",
					invitees: [],
					allowShopping: false,
				})
			);
			// FetchAllGroupCarts();
			// router.refresh();
			// // console.log(response?.data);
		} catch (error: any) {
			// console.log("API Error", error);
			toast.remove();
			toast.error(error?.response?.data?.error);
		}
	};

	const endShoppingByInvitee = async (inviteesToken: string) => {
		const endpoint = `${CREATE_GROUP_CART_API}/${inviteeToken}`;
		try {
			const response = await axios.put(
				endpoint,
				{},
				{
					headers: {
						Authorization: `Bearer ${user?.token}`,
					},
					params: {
						inviteeToken: inviteeToken,
					},
				}
			);
			// console.log(response?.data);
		} catch (error: any) {
			// console.log("API Error: ", error);
			toast.remove();
			toast.error(`An error occured: ${error?.response?.data?.error}`);
		}
	};

	const startShopping = async () => {
		const endpoint = `${CREATE_GROUP_CART_API}/${cartId}/start-shopping`;
		try {
			const response = await axios.put(
				endpoint,
				{},
				{
					headers: {
						Authorization: `Bearer ${user?.token}`,
					},
				}
			);
			toast.remove();
			toast.success(response?.data?.msg);
			dispatch(
				createGroupCart({
					...groupCart,
					allowShopping: response?.data?.result?.allowShopping,
				})
			);
			await FetchAllGroupCarts();
		} catch (error: any) {
			// console.log("API Error", error);
			toast.remove();
			toast.error(error?.response?.data?.error);
		}
	};

	const restartShopping = async (cartId: string | undefined) => {
		const endpoint = `${CREATE_GROUP_CART_API}/${cartId}/shop`;
		try {
			const response = await axios.post(
				endpoint,
				{},
				{
					headers: {
						Authorization: `Bearer ${user?.token}`,
					},
				}
			);
			dispatch(
				createGroupCart({
					allowShopping: response?.data?.result?.allowShopping,
					id: response?.data?.result?.id,
					totalPrice: response?.data?.result?.totalPrice,
					items: response?.data?.result?.items,
					invitees: response?.data?.result?.invitees,
					discount: response?.data?.result?.discount,
					owner: response?.data?.result?.owner,
				})
			);
			// console.log(response?.data);
			toast.remove();
			toast.success(response?.data?.msg);
			FetchAllGroupCarts();
		} catch (error: any) {
			// console.log("API Error: ", error);
			toast.remove();
			toast.error(error?.response?.data?.error);
		}
	};

	return {
		deleteGroupCartItem,
		addItemToGroupCart,
		increaseItemQuantity,
		decreaseItemQuantity,
		endShopping,
		startShopping,
		restartShopping,
		endShoppingByInvitee,
	};
};
export default useGroupCartProduct;
